import { Component, computed, inject, signal, type Signal, type WritableSignal } from "@angular/core";

import { TENANT_CONTEXT } from "../../../core/auth/tenant-context.token";
import {
  buildPortalUrl,
  buildStaffDoorUrl,
  type IBuildPortalUrlConfig,
} from "../../../core/support/build-portal-url";
import { normalizePortalSlug } from "../../../core/support/normalize-portal-slug";
import { AuthLayoutComponent } from "../../../shared/components/auth-layout/auth-layout.component";
import { MicrosoftMarkComponent } from "../../../shared/components/microsoft-mark/microsoft-mark.component";
import { environment } from "../../../../environments/environment";

import type { ITenantContext } from "../../../core/auth/i-tenant-context";

/**
 * The unknown-slug fallback — board 7f, right half.
 *
 * Reached when the host carries a customer slug that has no Auth0 organization
 * behind it: a mistyped subdomain, a customer that was never provisioned, or a
 * link that outlived its organization. Before this screen existed that state
 * surfaced as the login interstitial's "Something went wrong … this is usually
 * temporary", which is wrong twice over — nothing went wrong, and no amount of
 * waiting fixes it.
 *
 * WHAT THE PRIMARY ACTION DOES, and why it is not the board's control: the
 * board draws a "Work email" box, on the premise that an address can be mapped
 * to the organization that owns its domain. Nothing in the portal can do that
 * — organizations carry no email domain, and the endpoint that would answer it
 * would also answer "is <domain> a Davaco customer?" for the open internet,
 * which is the exact oracle `branding-router` returns Davaco defaults to avoid.
 * So the box takes the organization's portal ADDRESS instead and navigates
 * there. Same outcome for the visitor, no new public surface, and the button is
 * never dead.
 */
@Component({
  selector: "app-unknown-organization",
  standalone: true,
  imports: [AuthLayoutComponent, MicrosoftMarkComponent],
  templateUrl: "./unknown-organization.component.html",
  styleUrl: "./unknown-organization.component.scss",
})
export class UnknownOrganizationComponent {

  private readonly tenant: ITenantContext = inject(TENANT_CONTEXT);

  /** Exactly what is in the box, unprocessed. */
  public readonly entered: WritableSignal<string> = signal<string>("");

  /** The slug the box resolves to, or null while it cannot be one. */
  public readonly slug: Signal<string | null> = computed((): string | null =>
    normalizePortalSlug(this.entered())
  );

  /**
   * Only complain once there is something to complain about. Validating an
   * untouched box would open the screen with an error on it.
   */
  public readonly invalid: Signal<boolean> = computed(
    (): boolean => this.entered()
.trim().length > 0 && this.slug() === null
  );

  /** The address the visitor arrived on, named so they can see the typo. */
  public readonly attempted: string = this.tenant.organization ?? "";

  /** The apex host, shown under the box so the box's job is unambiguous. */
  public readonly baseHost: string = environment.baseHost;

  public onInput(event: Event): void {
    this.entered.set((event.target as HTMLInputElement).value);
  }

  /**
   * Leave for the organization the visitor named.
   *
   * A full-page navigation, not a router hop: deployed, an organization is a
   * different HOST, and the tenant context is resolved once at bootstrap from
   * that hostname. Routing within this app would keep the dead slug.
   */
  public go(event: Event): void {
    event.preventDefault();

    const slug: string | null = this.slug();
    if (slug === null) {
      return;
    }

    window.location.assign(buildPortalUrl(slug, this.locationConfig()));
  }

  /** Hand a Davaco employee to the Entra door on the apex host. */
  public goToStaffDoor(): void {
    window.location.assign(buildStaffDoorUrl(this.locationConfig()));
  }

  private locationConfig(): IBuildPortalUrlConfig {
    return {
      baseHost: environment.baseHost,
      currentHost: window.location.hostname,
      origin: window.location.origin,
    };
  }
}
